import type { ReactNode } from "react";
import type { RouteObject } from "src/router";
import type { RoutePreload } from "src/router/route-preloads";
import { Navigate } from "src/router";
import { FeatureFlagRoute } from "src/components/router/feature-flag-route";
import { PermissionedRoute } from "src/components/router/permissioned-route";
import { useLifecycleStudioEnabled } from "src/hooks/use-lifecycle-studio-enabled";
import { useAgentsEnabledFlag } from "src/pages/agents/hooks/use-agents-enabled-flag";
import { AdStudioDemoRoute } from "src/pages/ad-studio-demo/ad-studio-demo-route";
import { useIsAdStudioEnabled } from "src/pages/agents/hooks/use-is-ad-studio-enabled";
import { lazyNamed } from "src/utils/lazy-named";
import { OpportunitiesFeatureRoute } from "src/pages/ad-studio/components/opportunities-feature-route";

import { useExternalContextEnabled } from "./external-context-sources/use-external-context-enabled";

const ContextHubBrands = lazyNamed(
  () => import("./brands"),
  "ContextHubBrands",
);
const ContextHubLayout = lazyNamed(
  () => import("./context-agent/context-hub-layout"),
  "ContextHubLayout",
);
const contextHubContextImport = () => import("./context");
const ContextHubContext = lazyNamed(
  contextHubContextImport,
  "ContextHubContext",
);

export const preloads: RoutePreload[] = [
  ["/context-hub", contextHubContextImport],
];
const ContextHubConnections = lazyNamed(
  () => import("./connections"),
  "ContextHubConnections",
);
const ContextEntitiesSettings = lazyNamed(
  () => import("./context-entities"),
  "ContextEntitiesSettings",
);
const SkillsSettings = lazyNamed(
  () => import("./context-entities/skills"),
  "SkillsSettings",
);
const ContextReviewPage = lazyNamed(
  () => import("./context-review"),
  "ContextReviewPage",
);
const AlignmentPage = lazyNamed(
  () => import("src/pages/ad-studio-demo/alignment"),
  "AlignmentPage",
);
const AlignmentTestDetail = lazyNamed(
  () => import("src/pages/ad-studio-demo/alignment/alignment-detail"),
  "AlignmentTestDetail",
);
const CopyReferencesPage = lazyNamed(
  () => import("./copy-references"),
  "CopyReferencesPage",
);
const CopyReferencesDetailPage = lazyNamed(
  () => import("./copy-references/detail"),
  "CopyReferencesDetailPage",
);
const MemoriesSettings = lazyNamed(
  () => import("./memories"),
  "MemoriesSettings",
);
const GuardrailsSettings = lazyNamed(
  () => import("./guardrails"),
  "GuardrailsSettings",
);
const BestPracticesSettings = lazyNamed(
  () => import("./best-practices"),
  "BrandSettings",
);
const PersonalizationDataPage = lazyNamed(
  () => import("./personalization-data"),
  "PersonalizationDataPage",
);
const ExternalContextSourcesPage = lazyNamed(
  () => import("./external-context-sources"),
  "ExternalContextSourcesPage",
);
const AddContextFromSource = lazyNamed(
  () => import("./external-context-sources/add-from-source"),
  "AddContextFromSource",
);
const EditSemanticLayerContextSource = lazyNamed(
  () => import("./external-context-sources/edit-semantic-layer-context-source"),
  "EditSemanticLayerContextSource",
);
const SignalsSettings = lazyNamed(() => import("./signals"), "SignalsSettings");
const OpportunitiesSettings = lazyNamed(
  () => import("./opportunities"),
  "OpportunitiesSettings",
);
const StylesListPage = lazyNamed(() => import("./styles"), "StylesListPage");
const CatalogLayout = lazyNamed(
  () => import("./catalog/layout"),
  "CatalogLayout",
);
const CatalogPage = lazyNamed(() => import("./catalog"), "CatalogPage");
const CatalogFeedsPage = lazyNamed(
  () => import("./catalog/feeds"),
  "CatalogFeedsPage",
);
const StyleDetailPage = lazyNamed(
  () => import("./styles/detail"),
  "StyleDetailPage",
);
const IntegrationDrawer = lazyNamed(
  () => import("./integrations/components"),
  "IntegrationDrawer",
);

const ContextHubFeatureGate = ({ children }: { children: ReactNode }) => {
  const isAdStudioEnabled = useIsAdStudioEnabled();
  const isAgentsEnabled = useAgentsEnabledFlag();
  const isLifecycleStudioEnabled = useLifecycleStudioEnabled();

  if (!isAdStudioEnabled && !isAgentsEnabled && !isLifecycleStudioEnabled) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const AdsOrLifecycleFeatureGate = ({ children }: { children: ReactNode }) => {
  const isAdStudioEnabled = useIsAdStudioEnabled();
  const isLifecycleStudioEnabled = useLifecycleStudioEnabled();

  if (!isAdStudioEnabled && !isLifecycleStudioEnabled) {
    return <Navigate to="/context-hub/context" replace />;
  }

  return <>{children}</>;
};

const LifecycleStudioSettingsGate = ({ children }: { children: ReactNode }) => {
  const isLifecycleStudioEnabled = useLifecycleStudioEnabled();

  if (!isLifecycleStudioEnabled) {
    return <Navigate to="/context-hub/context/entities" replace />;
  }

  return <>{children}</>;
};

const ExternalContextGate = ({ children }: { children: ReactNode }) => {
  const isExternalContextEnabled = useExternalContextEnabled();

  if (!isExternalContextEnabled) {
    return <Navigate to="/context-hub/context/entities" replace />;
  }

  return <>{children}</>;
};

const AdStudioSettingsGate = ({ children }: { children: ReactNode }) => {
  const isAdStudioEnabled = useIsAdStudioEnabled();

  if (!isAdStudioEnabled) {
    return <Navigate to="/context-hub/context/entities" replace />;
  }

  return <>{children}</>;
};

const ContextHubIndexRedirect = () => {
  const isAdStudioEnabled = useIsAdStudioEnabled();
  const isLifecycleStudioEnabled = useLifecycleStudioEnabled();

  if (isAdStudioEnabled || isLifecycleStudioEnabled) {
    return <Navigate to="brands" replace />;
  }

  return <Navigate to="context" replace />;
};

export const routes: RouteObject[] = [
  {
    path: "context-hub",
    spacesAccess: "hidden-from-limited-view",
    element: (
      <PermissionedRoute
        permission={{
          v2: { resource: "workspace", grant: "can_configure_agents" },
        }}
        redirect="/"
      >
        <ContextHubFeatureGate>
          <ContextHubLayout />
        </ContextHubFeatureGate>
      </PermissionedRoute>
    ),
    children: [
      {
        index: true,
        spacesAccess: "hidden-from-limited-view",
        element: <ContextHubIndexRedirect />,
      },
      {
        path: "brands",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <AdsOrLifecycleFeatureGate>
            <ContextHubBrands />
          </AdsOrLifecycleFeatureGate>
        ),
      },
      {
        path: "styles",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <AdsOrLifecycleFeatureGate>
            <StylesListPage />
          </AdsOrLifecycleFeatureGate>
        ),
      },
      {
        path: "styles/:styleId",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <AdsOrLifecycleFeatureGate>
            <StyleDetailPage />
          </AdsOrLifecycleFeatureGate>
        ),
      },
      {
        path: "catalog",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <AdsOrLifecycleFeatureGate>
            <CatalogLayout />
          </AdsOrLifecycleFeatureGate>
        ),
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: <CatalogPage />,
          },
          {
            path: "feeds",
            spacesAccess: "hidden-from-limited-view",
            element: <CatalogFeedsPage />,
          },
          {
            path: ":catalogItemId",
            spacesAccess: "hidden-from-limited-view",
            element: <CatalogPage />,
          },
        ],
      },
      {
        // Nested under the tab's URL, but registered as a sibling of the
        // `context` layout route rather than a child: the detail page brings its
        // own page shell, and rendering it in the tabs' outlet would card-in-card.
        path: "context/copy-references/:channel",
        spacesAccess: "hidden-from-limited-view",
        element: <CopyReferencesDetailPage />,
      },
      {
        path: "context/external-context-sources/new",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <ExternalContextGate>
            <AddContextFromSource />
          </ExternalContextGate>
        ),
      },
      {
        path: "context/external-context-sources/:id/edit",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <ExternalContextGate>
            <EditSemanticLayerContextSource />
          </ExternalContextGate>
        ),
      },
      {
        path: "context",
        spacesAccess: "hidden-from-limited-view",
        element: <ContextHubContext />,
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: <Navigate to="entities" replace />,
          },
          {
            path: "entities",
            spacesAccess: "hidden-from-limited-view",
            element: <ContextEntitiesSettings />,
          },
          {
            path: "alignment",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <AdStudioDemoRoute redirect="/context-hub/context/entities">
                <AlignmentPage />
              </AdStudioDemoRoute>
            ),
          },
          {
            path: "alignment/:testId",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <AdStudioDemoRoute redirect="/context-hub/context/entities">
                <AlignmentTestDetail />
              </AdStudioDemoRoute>
            ),
          },
          {
            path: "skills",
            spacesAccess: "hidden-from-limited-view",
            element: <SkillsSettings />,
          },
          {
            path: "review",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <FeatureFlagRoute
                flagKey="appContextReviewEnabled"
                redirect="/context-hub/context/entities"
              >
                <ContextReviewPage />
              </FeatureFlagRoute>
            ),
          },
          {
            path: "copy-references",
            spacesAccess: "hidden-from-limited-view",
            element: <CopyReferencesPage />,
          },
          {
            path: "personalization-data",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <LifecycleStudioSettingsGate>
                <PersonalizationDataPage />
              </LifecycleStudioSettingsGate>
            ),
          },
          {
            path: "external-context-sources",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <ExternalContextGate>
                <ExternalContextSourcesPage />
              </ExternalContextGate>
            ),
          },
          {
            path: "memories",
            spacesAccess: "hidden-from-limited-view",
            element: <MemoriesSettings />,
          },
          {
            path: "guardrails",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <LifecycleStudioSettingsGate>
                <GuardrailsSettings />
              </LifecycleStudioSettingsGate>
            ),
          },
          {
            path: "best-practices",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <LifecycleStudioSettingsGate>
                <BestPracticesSettings />
              </LifecycleStudioSettingsGate>
            ),
          },
          {
            path: "signals",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <AdStudioSettingsGate>
                <SignalsSettings />
              </AdStudioSettingsGate>
            ),
          },
          {
            path: "opportunities",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <OpportunitiesFeatureRoute redirect="/context-hub/context/entities">
                <AdStudioSettingsGate>
                  <OpportunitiesSettings />
                </AdStudioSettingsGate>
              </OpportunitiesFeatureRoute>
            ),
          },
        ],
      },
      {
        path: "connections",
        spacesAccess: "hidden-from-limited-view",
        element: <ContextHubConnections />,
        children: [
          {
            path: "integrations/:type",
            spacesAccess: "hidden-from-limited-view",
            element: <IntegrationDrawer />,
          },
        ],
      },
    ],
  },
];
